import React, { useState } from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import DemoConvertModal from "./DemoConvertModal";
import { track } from "../utils/analytics";

/**
 * DemoActionModal — the "that's a demo-only action" nudge.
 *
 * Shown by DemoGuardProvider whenever a demo visitor tries to do
 * something that only makes sense on a real, owned CRM: add their own
 * records, change account info, connect Google/Gmail, etc. It explains
 * that the demo is a look-around tour of sample data and points them at
 * the one thing that unlocks the real thing — picking a plan.
 *
 * Non-blocking by design: it's a single modal they dismiss with "Keep
 * exploring," not a hard wall. The hard wall is the expired gate
 * (DemoBanner); this is a gentle "not in the tour" tap on the shoulder.
 */
export default function DemoActionModal({ open, action, onClose }) {
  const [convertOpen, setConvertOpen] = useState(false);

  if (!open) return null;

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-action-title"
        className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
        onClick={onClose}
      >
        <div
          className="bg-[#141b2b] border border-white/10 rounded-2xl max-w-sm w-full shadow-2xl p-6 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-12 h-12 mx-auto rounded-xl bg-[#1ec8c8]/15 border border-[#1ec8c8]/30 flex items-center justify-center mb-4">
            <Sparkles className="w-6 h-6 text-[#1ec8c8]" />
          </div>
          <h3 id="demo-action-title" className="text-white font-bold text-lg mb-2">
            That's a real-CRM action
          </h3>
          <p className="text-gray-300 text-sm mb-5 leading-relaxed">
            {action ? <span className="text-white font-semibold">{action}</span> : "That"}{" "}
            isn't part of the demo. This is a tour of sample data — you can
            click around and move things, but adding your own info or
            connecting accounts comes with your own CRM. Pick a plan and
            we'll set you up.
          </p>
          <button
            type="button"
            onClick={() => { track("demo_action_blocked_convert_click", { action: action || null }); setConvertOpen(true); }}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#1ec8c8] hover:bg-[#34b8b3] text-black font-bold transition-colors"
          >
            Pick a plan to get your CRM
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full mt-3 text-sm text-gray-400 hover:text-white"
          >
            Keep exploring the demo
          </button>
        </div>
      </div>

      <DemoConvertModal open={convertOpen} onClose={() => setConvertOpen(false)} />
    </>
  );
}
